import { useCallback, useMemo, useState } from 'react'

import { Typography } from '@twistopayments/design-system/components/atoms/typography'

import type { EshopListItemAPI, SectionsEshopsComponent } from '@twisto/api-cms'

import { Button } from 'homepage/components/atoms'
import { useAdditionalData } from 'homepage/libs/context'
import { useHighlightedText } from 'homepage/libs/hooks'
import { CATEGORY_TO_INCLUDE_PARAM } from 'homepage/utils'

import { EshopCard } from '../../eshop-card'
import EshopsNavigation from '../../molecules/eshops-navigation'
import type { EshopSelection } from '../../molecules/eshops-navigation/types'

import { EshopsByCategory } from './components/eshops-categories'
import { EshopsList } from './components/eshops-list'
import { SearchResults } from './components/search-results'
import { getEshopLists, getSelectedEshops } from './eshops.helpers'
import * as styles from './eshops.styles'

export const Eshops = ({
  headline,
  cta,
  titleHighlight,
  description,
  category,
  showNavigation,
  showSearch,
  navigation,
}: SectionsEshopsComponent) => {
  const { eshopsData, origin, staticTexts } = useAdditionalData()

  const { before, highlight, after } = useHighlightedText(
    headline,
    titleHighlight
  )

  const eshops = useMemo(
    () =>
      category && eshopsData
        ? eshopsData[CATEGORY_TO_INCLUDE_PARAM[category]]
        : [],
    [category, eshopsData]
  )

  const allEshops = eshopsData?.eshops

  const sortedCategories = useMemo(
    () =>
      [...(eshopsData?.eshop_categories || [])].sort((a, b) => {
        if (a.order != null && b.order != null) {
          return a.order - b.order
        }

        return (a.slug || '').localeCompare(b.slug || '')
      }),
    [eshopsData?.eshop_categories]
  )

  const [selection, setSelection] = useState<EshopSelection>({ type: 'all' })
  const eshopsByCategory = useMemo(
    () => getSelectedEshops(selection, eshopsData),
    [selection, eshopsData]
  )
  const [filteredEshops, setFilteredEshops] = useState<EshopListItemAPI[]>([])

  const normalizeField = (field: string) =>
    field.toLowerCase().replace(/ /g, '')

  const filterEshops = useCallback(
    (searchTerm: string) => {
      if (searchTerm && allEshops) {
        const normalizedSearchTerm = searchTerm.toLowerCase().replace(/\s/g, '')

        setFilteredEshops(
          allEshops.filter((eshop) => {
            const normalizedKeywords = normalizeField(eshop.keywords.join(' '))
            const normalizedName = normalizeField(eshop.name)

            return (
              normalizedKeywords.includes(normalizedSearchTerm) ||
              normalizedName.includes(normalizedSearchTerm)
            )
          })
        )
      } else {
        setFilteredEshops([])
      }
    },
    [allEshops]
  )

  const handleSelectionChange = useCallback((selection: EshopSelection) => {
    setSelection(selection)
    setFilteredEshops([])
  }, [])

  const renderEshops = useMemo(() => {
    if (filteredEshops.length) {
      return <SearchResults eshops={filteredEshops} />
    }

    if (selection.type !== 'all') {
      return (
        <EshopsByCategory
          categoryName={
            selection.type === 'pay-in-3'
              ? staticTexts?.payInThree
              : selection.category.name
          }
          eshops={eshopsByCategory}
        />
      )
    }

    return getEshopLists(eshopsData, staticTexts).map(
      ({ field, title, eshops }) => (
        <EshopsList key={field} list={eshops} name={title} />
      )
    )
  }, [filteredEshops, selection, eshopsByCategory, eshopsData, staticTexts])

  return (
    <>
      {showNavigation && category === 'all' && (
        <EshopsNavigation
          categories={sortedCategories}
          handleSearch={filterEshops}
          hasSearch={showSearch}
          navigation={navigation}
          selection={selection}
          onSelect={handleSelectionChange}
        />
      )}
      <section css={[styles.eshops, styles.container]}>
        {category === 'all' ? (
          <main>{renderEshops}</main>
        ) : (
          <main>
            <Typography variant="h2">
              {before}
              <span>{highlight}</span>
              {after}
            </Typography>

            <div css={styles.desc}>{description}</div>

            {eshops && (
              <div css={styles.shopsWrap}>
                <div css={styles.shops}>
                  {eshops.map((shop) => (
                    <EshopCard
                      key={shop.id}
                      origin={origin || ''}
                      shop={shop}
                    />
                  ))}
                </div>
              </div>
            )}

            <div css={[styles.centered]}>
              {cta?.map((button) => <Button key={button.id} {...button} />)}
            </div>
          </main>
        )}
      </section>
    </>
  )
}
